/* eslint-disable */
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Typography } from 'tfc-components';

import MainLayout from 'components/templates/MainLayout';
import ResendSMSQRCode from 'components/templates/ResendSMSQRCode';
import Confirm from 'pages/Confirm';
import Register, { CheckInvitationType } from 'pages/Register';
import UserInfo, { UserInfoType } from 'pages/UserInfo';
import { checkInvitationServiceV2, checkinServiceV2, resendSMSQrCodeServiceV2 } from 'services/checkin';
import {
  CheckInvitationData,
  CheckInvitationParams,
  CheckinParams,
} from 'services/checkin/types';
import returnValidateErrorMessages from 'services/common/errors';
import { parseToYYYYMMDD } from 'utils/functions';
import useSchemas from 'utils/shemas';
import NotifyModal from 'components/templates/NotifyModal';

const fieldToText = (field: string) => {
  switch (field) {
    case 'invitation_code':
      return 'Mã CME';
    case 'phone':
      return 'Số điện thoại';
    case 'full_name':
      return 'Họ và tên';
    case 'position_or_degree':
      return 'Vị trí / bằng cấp';
    case 'hospital':
      return 'Bệnh viện';
    case 'department':
      return 'Phòng ban';
    case 'provinceCode':
      return 'Tỉnh/Thành phố';
    default:
      return '';
  }
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { userInfoSchema, checkInvitationSchema } = useSchemas();
  const checkMethod = useForm<CheckInvitationType>({
    mode: 'onChange',
    defaultValues: {
      phone: '',
    },
    resolver: yupResolver(checkInvitationSchema),
  });
  const checkinMethod = useForm<UserInfoType>({
    mode: 'onChange',
    resolver: yupResolver(userInfoSchema),
    defaultValues: {
      invitation_code: '',
      full_name: '',
      phone: '',
      hospital: '',
      department: '',
      provinceCode: undefined,
      isRequireCme: true,
    },
  });

  /* States */
  const [step, setStep] = React.useState<number>(0);
  const [errorText, setErrorText] = React.useState<string>('');
  const [isCheckin, setIsCheckin] = React.useState<boolean>(false);
  const [isTemporary, setIsTemporary] = React.useState<boolean>(false);
  const [isResendSMS, setIsResendSMS] = React.useState<{
    open: boolean,
    phone?: string,
    description?: string,
    status?: 'success' | 'error',
  }>({
    open: false,
    phone: '',
    description: '',
    status: undefined,
  });

  const [errorRegister, setErrorRegister] = React.useState<{
    message: string;
    isShow: boolean;
  }>({
    isShow: false,
    message: '',
  });

  const { mutate: checkInvitationMutate, isLoading: isLoadingCheckInvitation } = useMutation(
    ['checkInvitationMutate'],
    async (data: CheckInvitationParams) => checkInvitationServiceV2({
      invitation_code: data.invitation_code ?? '',
      phone: data.phone ?? '',
    }),
    {
      onSuccess: (data: CheckInvitationData) => {
        setStep(1);
        checkinMethod.setError('invitation_code', {});
        if (data) {
          checkinMethod.reset({
            invitation_code: data?.code,
            full_name: data?.fullName,
            phone: data?.phone,
            position_or_degree: data?.positionOrDegree || '',
            hospital: data?.hospital || '',
            department: data?.department || '',
            provinceCode: {
              label: data?.province?.name,
              value: data?.province?.code,
            },
            dob: data?.dob ? new Date(parseToYYYYMMDD(data?.dob)) : undefined,
            isRequireCme: true,
          });
        } else {
          checkinMethod.reset({});
        }
      },
      onError: (error: any, variables: CheckInvitationParams) => {
        const errorsRes = error?.response?.data?.errors;
        if (error?.length > 0) {
          error.forEach((err: any) => {
            if (err.code === 'existed') {
              checkMethod.setError('phone', {
                type: 'custom',
                message: 'Đã checkin tham dự chương trình',
              });
            }
            if (err.code === 'userRegistered') {
              setIsResendSMS({
                open: true,
                phone: variables.phone,
                description: '',
                status: undefined,
              });
            }
          });
        } else if (errorsRes?.length > 0) {
          if (errorsRes[0]?.code === 'CAMPAIGN_NOT_FOUND') {
            checkMethod.setError('phone', {
              type: 'custom',
              message: 'Chưa có chương trình diễn ra',
            });
            return;
          }
          if (errorsRes[0]?.code === 'checkinNotFound') {
            checkMethod.setError('phone', {
              type: 'custom',
              message: 'Số điện thoại chưa có trong hệ thống. Vui lòng liên hệ nhân viên Abbott để được hỗ trợ.',
            });
          }
        } else {
          checkMethod.setError('phone', {
            type: 'custom',
            message: 'Đã có lỗi xảy ra, hãy thử lại !',
          });
        }
      },
    }
  );

  const { mutate: checkinMutate, isLoading } = useMutation(
    ['checkinMutate'],
    async (data: CheckinParams) => checkinServiceV2(data),
    {
      onSuccess: (res) => {
        navigate(`/${res.routeCode}`);
      },
      onError(error: any) {
        const errorsRes = error?.response?.data?.errors;
        if (error.length > 0) {
          if (error[0]?.code === 'existed') {
            setIsCheckin(true);
            return;
          }
          error.forEach((err: ValidateError) => {
            checkinMethod.setError(err.field, {
              message: returnValidateErrorMessages(err, fieldToText(err.field)),
            });
            const confirmErr = ['signature'];
            if (!confirmErr.includes(err.field)) {
              setStep(1);
            }
          });
        } else if (errorsRes?.length > 0) {
          if (errorsRes[0]?.code === 'sendSMSError') {
            setErrorRegister({
              isShow: true,
              message: 'Hệ thống đã có lỗi xảy ra.<br /> Vui lòng liên hệ nhân viên Abbott để được hỗ trợ',
            });
            return;
          }
          if (errorsRes[0]?.code === 'CAMPAIGN_NOT_FOUND') {
            setErrorRegister({
              isShow: true,
              message: 'Chưa có chương trình diễn ra',
            });
            return;
          }
          setErrorRegister({
            isShow: true,
            message: 'Đã có lỗi xảy ra.<br /> Vui lòng liên hệ nhân viên Abbott để được hỗ trợ',
          });
        }
      },
    }
  );

  const handleCheck = async (data: CheckInvitationType) => {
    if (data.phone) {
      checkInvitationMutate({
        phone: data.phone,
      });
    }
  };

  const handleRegister = () => {
    checkinMethod.setError('invitation_code', {});
    checkinMethod.setValue('invitation_code', '');
    checkinMethod.setValue('phone', checkMethod.getValues('phone') ?? '');
    checkinMethod.setValue('isRequireCme', false);
    setIsTemporary(true);
    setStep(1);
  };

  const handleConfirm = () => {
    setStep(2);
  };

  // const handleComplete = () => {
  // window.location.reload();
  // };

  const handleSubmit = (data: CheckinParams) => {
    checkinMutate(data);
  };

  const { mutate: resendSMSMutate } = useMutation(
    ['resendSMSMutate'],
    async (params: { phone: string }) => resendSMSQrCodeServiceV2({
      phone: params.phone,
    }),
    {
      onSuccess: (_, variables) => {
        setIsResendSMS({
          open: true,
          phone: variables.phone,
          description: 'Đã gửi lại tin nhắn thành công',
          status: 'success',
        });
      },
      onError: (_: any, variables) => {
        setIsResendSMS({
          open: true,
          phone: variables.phone,
          description: 'Gửi SMS thất bại. Vui lòng liên hệ số hotline để được hỗ trợ',
          status: 'error',
        });
        // if (error[0]?.code === 'sendSMSFailed') {
        // setIsResendSMS({
        // open: true,
        // phone: variables.phone,
        // description: 'Gửi SMS thất bại. Vui lòng liên hệ số hotline để được hỗ trợ',
        // status: 'error',
        // });
        // } else {
        // setIsResendSMS({
        // open: true,
        // phone: variables.phone,
        // description: 'Gửi SMS thất bại. Vui lòng liên hệ số hotline để được hỗ trợ',
        // status: 'error',
        // });
        // }
      },
    }
  );

  const handleResendSMS = () => {
    if (isResendSMS.phone) {
      resendSMSMutate({ phone: isResendSMS.phone || '' });
    }
  };

  const textButtonResendSMS = () => {
    if (isResendSMS.status === 'success' || isResendSMS.status === 'error') {
      return 'Đóng';
    }
    return 'Gửi lại';
  };

  return (
    <MainLayout
      showHotline
      onBack={{
        onClick: () => {
          if (step === 1) {
            setStep(0);
          }
        },
        show: step === 1,
      }}
    >
      {/* <Typography.Heading
	textStyle="center"
	fontweight="600"
	extendClasses="t-mainLayout_subHeading fs-36x44"
	>
		{step === 2 ? 'XÁC NHẬN' : 'BIỂU MẪU'}
		{' '}
		ĐĂNG KÍ THAM DỰ
		<br />
		CHƯƠNG TRÌNH
	</Typography.Heading> */}
      <div className="p-register">
        {step === 0 && (
          <Typography.Text
            textStyle="italic"
            extendClasses="fs-26x36 p-register_confirmText"
          >
            Vui lòng nhập số điện thoại
            <br />
            đã đăng ký với Abbott
          </Typography.Text>
        )}
        {step === 1 && (
          <Typography.Text
            textStyle="italic"
            extendClasses="fs-26x36 p-register_confirmText"
          >
            {`Vui lòng ${isTemporary ? 'điền' : 'kiểm tra'
              } thông tin bên dưới và xác nhận`}
          </Typography.Text>
        )}
        {(() => {
          switch (step) {
            case 0:
              return (
                <Register
                  method={checkMethod}
                  handleCheck={handleCheck}
                  errorText={
                    checkinMethod.formState.errors.invitation_code?.message
                  }
                  handleRegister={handleRegister}
                  errorTextType={errorText}
                  loading={isLoadingCheckInvitation}
                />
              );
            case 1:
              return (
                <UserInfo
                  method={checkinMethod}
                  handleConfirm={handleConfirm}
                  handleCheck={handleCheck}
                  isTemporary={isTemporary}
                />
              );
            case 2:
              return (
                <Confirm
                  checkinMethod={checkinMethod}
                  handleSubmit={handleSubmit}
                  isCheckin={isCheckin}
                  loading={isLoading}
                />
              );
            default:
              return null;
          }
        })()}
      </div>
      <ResendSMSQRCode
        isOpen={isResendSMS.open}
        submitText={textButtonResendSMS()}
        status={isResendSMS.status}
        handleSubmit={handleResendSMS}
        handleClose={() => {
          setIsResendSMS({
            ...isResendSMS,
            open: false,
            phone: '',
          });
          checkMethod.reset({});
        }}
        desc={isResendSMS.description || 'Số điện thoại đã được đăng ký. Quý khách có muốn gửi lại tin nhắn có mã QR đăng ký không?'}
      />
      <NotifyModal
        isOpen={errorRegister.isShow}
        variant="transparent"
        desc={errorRegister.message}
        isError
        backText="Đóng"
        handleBack={() => {
          setErrorRegister({
            ...errorRegister,
            isShow: false,
          });
        }}
      />
    </MainLayout>
  );
};

export default Home;
